import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress,
  MenuItem, IconButton, Tooltip, Divider, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;

const ESTADO_COLOR = { BORRADOR: 'default', CALCULADA: 'primary', CERRADA: 'success' };
const ESTADO_LABEL = { BORRADOR: 'Borrador', CALCULADA: 'Calculada', CERRADA: 'Cerrada' };

const aniosDisponibles = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2].map(String);
};

const mesesList = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const mesLabel = { '01':'Enero','02':'Febrero','03':'Marzo','04':'Abril','05':'Mayo','06':'Junio','07':'Julio','08':'Agosto','09':'Septiembre','10':'Octubre','11':'Noviembre','12':'Diciembre' };

export default function PlanillaRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [planillas, setPlanillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Nueva planilla
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaMes, setNuevaMes] = useState('');
  const [nuevaFechaPago, setNuevaFechaPago] = useState('');

  // Detalle
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [planillaActiva, setPlanillaActiva] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [detalleLoading, setDetalleLoading] = useState(false);

  // Editar línea
  const [editOpen, setEditOpen] = useState(false);
  const [editLine, setEditLine] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/planillas', { params: { empresa_id: empresaId, anio } });
      setPlanillas(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, anio]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;

  const handleNueva = async () => {
    setError('');
    try {
      const periodo = `${anio}-${nuevaMes}`;
      await axios.post('/api/rrhh/planillas', { empresa_id: empresaId, periodo, fecha_pago: nuevaFechaPago || null });
      setNuevaOpen(false); setNuevaMes(''); setNuevaFechaPago('');
      setSuccess('Planilla creada. Haz clic en "Calcular" para procesar.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al crear'); }
  };

  const handleCalcular = async (id) => {
    setError(''); setSuccess('');
    try {
      const r = await axios.post(`/api/rrhh/planillas/${id}/calcular`);
      setSuccess(`Planilla calculada: ${r.data.trabajadores} trabajadores procesados.`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular'); }
  };

  const handleCerrar = async (id) => {
    if (!window.confirm('¿Cerrar planilla? Ya no podrá modificarse.')) return;
    try {
      await axios.put(`/api/rrhh/planillas/${id}/cerrar`);
      setSuccess('Planilla cerrada correctamente.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al cerrar'); }
  };

  const handleReabrir = async (id) => {
    try {
      await axios.put(`/api/rrhh/planillas/${id}/reabrir`);
      setSuccess('Planilla reabierta.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al reabrir'); }
  };

  const handleVerDetalle = async (planilla) => {
    setPlanillaActiva(planilla);
    setDetalleOpen(true);
    setDetalleLoading(true);
    try {
      const r = await axios.get(`/api/rrhh/planillas/${planilla.id}`);
      setDetalle(Array.isArray(r.data.detalle) ? r.data.detalle : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar detalle'); }
    finally { setDetalleLoading(false); }
  };

  const handleEditarLinea = (line) => {
    setEditLine(line);
    setEditForm({
      horas_extra_25: line.horas_extra_25, horas_extra_35: line.horas_extra_35,
      minutos_tardanza: line.minutos_tardanza, dias_falta: line.dias_falta,
      movilidad: line.movilidad, otros_haberes: line.otros_haberes,
      descuento_prestamo: line.descuento_prestamo, otros_descuentos: line.otros_descuentos,
      observaciones: line.observaciones || ''
    });
    setEditOpen(true);
  };

  const handleGuardarLinea = async () => {
    try {
      await axios.put(`/api/rrhh/planillas/detalle/${editLine.id}`, editForm);
      setEditOpen(false);
      // Recargar detalle
      const r = await axios.get(`/api/rrhh/planillas/${planillaActiva.id}`);
      setDetalle(Array.isArray(r.data.detalle) ? r.data.detalle : []);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
  };

  const ef = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const descargarPdf = async (url) => {
    setError('');
    try {
      const r = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          setError(JSON.parse(text).error || 'Error al generar el PDF');
        } catch { setError('Error al generar el PDF'); }
      } else {
        setError(e.response?.data?.error || 'Error al generar el PDF');
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1400 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Planilla Mensual</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Filtros */}
        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas} getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField select label="Año" size="small" value={anio} onChange={e => setAnio(e.target.value)} sx={{ minWidth: '8em' }}>
              {aniosDisponibles().map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNuevaOpen(true)} disabled={!empresaId}>
              Nueva Planilla
            </Button>
          </Box>
        </Paper>

        {/* Lista planillas */}
        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Trabajadores</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bruto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Descuentos</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Neto a Pagar</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>EsSalud</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>F. Pago</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {planillas.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay planillas para {anio}</TableCell></TableRow>
                  ) : planillas.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{p.periodo}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={ESTADO_LABEL[p.estado] || p.estado} color={ESTADO_COLOR[p.estado] || 'default'} />
                      </TableCell>
                      <TableCell align="center">{p.num_trabajadores ?? 0}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_bruto)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#c62828' }}>{fmt(p.total_descuentos)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32' }}>{fmt(p.total_neto)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_essalud)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.82em' }}>{p.fecha_pago ? p.fecha_pago.slice(0, 10) : '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => handleVerDetalle(p)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        {p.estado !== 'CERRADA' && (
                          <Tooltip title="Calcular planilla">
                            <IconButton size="small" color="primary" onClick={() => handleCalcular(p.id)}><CalculateIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {p.estado === 'CALCULADA' && (
                          <Tooltip title="Cerrar planilla">
                            <IconButton size="small" color="success" onClick={() => handleCerrar(p.id)}><LockIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {p.estado === 'CERRADA' && (
                          <Tooltip title="Reabrir planilla">
                            <IconButton size="small" color="warning" onClick={() => handleReabrir(p.id)}><LockOpenIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {p.estado === 'CERRADA' && (
                          <Tooltip title="Descargar todas las boletas (PDF)">
                            <IconButton size="small" color="error" onClick={() => descargarPdf(`/api/rrhh/planillas/${p.id}/boletas-lote`)}>
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog Nueva Planilla */}
      <Dialog open={nuevaOpen} onClose={() => setNuevaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Nueva Planilla</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Mes" size="small" value={nuevaMes} onChange={e => setNuevaMes(e.target.value)} sx={{ minWidth: '16em' }}>
              {mesesList.map(m => <MenuItem key={m} value={m}>{mesLabel[m]}</MenuItem>)}
            </TextField>
            <TextField label="Fecha de Pago (opcional)" size="small" type="date" InputLabelProps={{ shrink: true }}
              value={nuevaFechaPago} onChange={e => setNuevaFechaPago(e.target.value)} sx={{ minWidth: '16em' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevaOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleNueva} disabled={!nuevaMes}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalle Planilla */}
      <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
          Detalle Planilla — {planillaActiva?.periodo}
          <Chip size="small" label={ESTADO_LABEL[planillaActiva?.estado] || ''} color={ESTADO_COLOR[planillaActiva?.estado] || 'default'} sx={{ ml: 1 }} />
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {detalleLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: '#e3f2fd', zIndex: 1 }}>Trabajador</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Básico</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Asig.Fam</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>H.Extra</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Otros</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bruto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>AFP/ONP</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>Desc.</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>5ta Cat</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Neto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>EsSalud</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prov.CTS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prov.Gratif</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Boleta</TableCell>
                    {planillaActiva?.estado !== 'CERRADA' && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Edit</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalle.map(d => (
                    <TableRow key={d.id} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, minWidth: 180 }}>
                        <Typography variant="body2" fontWeight="bold">{d.trabajador_nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.tipo_doc} {d.num_doc} · {d.sistema_pensiones === 'AFP' ? d.afp_nombre || 'AFP' : 'ONP'}</Typography>
                      </TableCell>
                      {[d.remuneracion_basica, d.asignacion_familiar, d.importe_horas_extra, d.otros_haberes + d.movilidad, d.total_bruto].map((v, i) => (
                        <TableCell key={i} align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(v || 0).toFixed(2)}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>
                        {Number((d.aporte_onp || 0) + (d.aporte_afp_obligatorio || 0) + (d.comision_afp || 0) + (d.seguro_afp || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>
                        {Number((d.descuento_tardanza || 0) + (d.descuento_falta || 0) + (d.descuento_prestamo || 0) + (d.otros_descuentos || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>{Number(d.retencion_quinta || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', fontWeight: 'bold', color: '#2e7d32' }}>{Number(d.neto_a_pagar || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(d.aporte_essalud || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(d.provision_cts || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(d.provision_gratificacion || 0).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Descargar boleta PDF">
                          <IconButton size="small" color="error" onClick={() => descargarPdf(`/api/rrhh/planillas/detalle/${d.id}/boleta`)}>
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      {planillaActiva?.estado !== 'CERRADA' && (
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEditarLinea(d)}><EditIcon fontSize="small" /></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar Línea */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Editar — {editLine?.trabajador_nombre}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="H. Extra 25%" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.horas_extra_25 || ''} onChange={e => ef('horas_extra_25', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="H. Extra 35%" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.horas_extra_35 || ''} onChange={e => ef('horas_extra_35', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Min. Tardanza" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.minutos_tardanza || ''} onChange={e => ef('minutos_tardanza', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Días de Falta" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.dias_falta || ''} onChange={e => ef('dias_falta', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Movilidad (S/)" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.movilidad || ''} onChange={e => ef('movilidad', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Otros Haberes (S/)" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.otros_haberes || ''} onChange={e => ef('otros_haberes', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Desc. Préstamo (S/)" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.descuento_prestamo || ''} onChange={e => ef('descuento_prestamo', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Otros Descuentos (S/)" size="small" type="number" sx={{ minWidth: '10em' }}
                value={editForm.otros_descuentos || ''} onChange={e => ef('otros_descuentos', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" size="small" multiline rows={2}
                value={editForm.observaciones || ''} onChange={e => ef('observaciones', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarLinea}>Recalcular y Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
